<template>
    <div class="container-fluid py-4" v-if="factura">
        <div class="row">
            <div class="col-12">
                <div class="card">
                    <div class="card-header pb-0">
                        <div class="row">
                            <div class="col-12 col-md-8">
                                <h6>Detalle de Factura</h6>
                                <p class="text-sm mb-0">Factura #{{ factura.header.full_number }}</p>
                            </div>

                            <div class="col-12 col-md-2 text-md-end mt-2 mt-md-0">
                                <argon-button
                                class="btn btn-success w-100 w-md-auto"
                                @click="printPDF"
                                size="sm"
                                >
                                <i class="fa fa-print me-1"></i>
                                Imprimir
                                </argon-button>
                            </div>

                            <div class="col-12 col-md-2 text-md-end mt-2 mt-md-0">
                                <argon-button
                                class="btn btn-secondary w-100 w-md-auto"
                                @click="goBack"
                                size="sm"
                                >
                                <i class="fa fa-arrow-left me-1"></i>
                                Volver
                                </argon-button>
                            </div>
                            </div>

                    </div>
                    <div class="card-body  pb-2">
                        <!-- Header de la Factura -->
                        <div class="row mb-4">
                            <div class="col-md-6">
                                <h6 class="mb-2">Información de la Empresa</h6>
                                <p class="text-sm mb-1"><strong>NIT:</strong> {{ factura.header.emitter_nit }}</p>
                                <p class="text-sm mb-1"><strong>Nombre:</strong> {{ factura.header.emitter_name }}</p>
                                <!-- Agrega más info de empresa si la tienes, ej. dirección, teléfono -->
                            </div>
                            <div class="col-md-6">
                                <h6 class="mb-2">Información del Cliente</h6>
                                <p class="text-sm mb-1"><strong>NIT:</strong> {{ factura.header.client_nit }}</p>
                                <p class="text-sm mb-1"><strong>Nombre:</strong> {{ factura.header.client_name }}</p>
                                <p class="text-sm mb-1"><strong>Tipo:</strong> {{ factura.header.client_type || 'N/A' }}</p>
                                <!-- Si tienes más datos del client (ej. email, phone), agrégalos aquí -->
                            </div>
                        </div>

                        <!-- Detalles de la Factura -->
                        <div class="row mb-4">
                            <div class="col-md-6">
                                <p class="text-sm mb-1"><strong>Tipo:</strong> {{ factura.header.invoice_type === 'FACTURA' ? 'Factura' : 'Ticket' }}</p>
                                <p class="text-sm mb-1"><strong>Fecha de Emisión:</strong> {{ formatDate(factura.header.date_issued) }}</p>
                                <p class="text-sm mb-1"><strong>Moneda:</strong> {{ factura.header.currency }}</p>
                                <p class="text-sm mb-1">
                                    <strong>Estado:</strong> 
                                    <span :class="getStateClass(factura.header.state)">
                                        {{ getStateText(factura.header.state) }}
                                    </span>
                                </p>
                            </div>
                            <div class="col-md-6 text-end">
                                <p class="text-sm mb-1"><strong>Serie/Número:</strong> {{ factura.header.series }}-{{ factura.header.number }}</p>
                                <!-- Agrega más detalles si es necesario, ej. traceability_code -->
                            </div>
                        </div>

                        <!-- Tabla de Líneas (Detalle) -->
                        <div class="table-responsive">
                            <table class="table align-items-center mb-0">
                                <thead>
                                    <tr>
                                        <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Descripción</th>
                                        <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Cantidad</th>
                                        <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Precio Unit.</th>
                                        <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Descuento</th>
                                        <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Impuesto (%)</th>
                                        <th class="text-right text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Total Línea</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(line, index) in factura.lines" :key="index">
                                        <td>
                                            <p class="text-xs font-weight-bold mb-0">{{ line.description }}</p>
                                            <!-- Si tienes product_id, puedes mostrar SKU o nombre del producto -->
                                        </td>
                                        <td class="text-center">
                                            <p class="text-xs font-weight-bold mb-0">{{ line.quantity.toFixed(4) }}</p>
                                        </td>
                                        <td class="text-center">
                                            <p class="text-xs font-weight-bold mb-0">${{ line.unit_price.toFixed(2) }}</p>
                                        </td>
                                        <td class="text-center">
                                            <p class="text-xs font-weight-bold mb-0">${{ line.discount.toFixed(2) }}</p>
                                        </td>
                                        <td class="text-center">
                                            <p class="text-xs font-weight-bold mb-0">{{ line.tax_rate }}%</p>
                                        </td>
                                        <td class="text-right">
                                            <p class="text-xs font-weight-bold mb-0">${{ line.line_total.toFixed(2) }}</p>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <!-- Totales -->
                        <div class="row mt-4">
                            <div class="col-md-8"></div>
                            <div class="col-md-4">
                                <div class="table-responsive">
                                    <table class="table align-items-left mb-0">
                                        <tbody>
                                            <tr>
                                                <td class="text-right"><strong>Subtotal:</strong></td>
                                                <td class="text-left">${{ factura.header.subtotal.toFixed(2) }}</td>
                                            </tr>
                                            <tr>
                                                <td class="text-right"><strong>Impuestos:</strong></td>
                                                <td class="text-left">${{ factura.header.total_taxes.toFixed(2) }}</td>
                                            </tr>
                                            <tr class="font-weight-bold">
                                                <td class="text-right"><strong>Total:</strong></td>
                                                <td class="text-left text-success">${{ factura.header.total.toFixed(2) }}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        <!-- Notas adicionales (opcional) -->
                        <div class="row mt-3" v-if="factura.header.dte_json || factura.header.dte_signature">
                            <div class="col-12">
                                <h6 class="mb-2">Información DTE (Facturación Electrónica)</h6>
                                <p class="text-sm mb-1" v-if="factura.header.dte_traceability_code">
                                    <strong>Código de Rastreo:</strong> {{ factura.header.dte_traceability_code }}
                                </p>
                                <!-- Puedes mostrar dte_json parseado si es necesario -->
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <div v-else class="container-fluid py-4">
        <div class="alert alert-warning">
            <h6>Factura no encontrada</h6>
            <p>El ID de factura proporcionado no existe (solo IDs 1 o 2 son válidos para simulación). <a href="/facturacion">Volver a la lista</a>.</p>
        </div>
    </div>
</template>

<script>
// Importa ArgonButton si lo usas
import ArgonButton from "@/components/ArgonButton.vue";

// Importa jsPDF y autotable para tablas
import jsPDF from 'jspdf';
import 'jspdf-autotable';  // Para generar tablas fácilmente

export default {
    name: "factura-detail",
    components: {
        ArgonButton
    },
    data() {
        return {
            factura: null,
            // Datos simulados (hardcodeados para simulación, como en tus ejemplos)
            clients: [
                {
                    id: 1,
                    nit: "1234567-8",
                    name: "Pedro López",
                    business_name: null,
                    email: "pedro@cliente.com",
                    phone: "555-0101",
                    fiscal_address: "Calle 1, Ciudad",
                    commercial_address: "Avenida Principal 10, Ciudad",
                    type: "NATURAL",
                    created_at: "2024-01-01 00:00:00",
                    updated_at: "2024-01-01 00:00:00"
                },
                {
                    id: 2,
                    nit: "9012345-6",
                    name: "Empresa ABC S.A.",
                    business_name: "Empresa ABC S.A.",
                    email: "contacto@abc.com",
                    phone: "555-0202",
                    fiscal_address: "Calle Fiscal 2, Ciudad",
                    commercial_address: "Calle Comercial 20, Ciudad",
                    type: "JURIDICA",
                    created_at: "2024-01-01 00:00:00",
                    updated_at: "2024-01-01 00:00:00"
                }
            ],
            invoices: [
                {
                    id: 1,
                    invoice_type: "FACTURA",
                    series: "F",
                    number: 1,
                    full_number: "F-001",
                    emitter_nit: "12345678-9",
                    emitter_name: "Mi Empresa POS",
                    client_id: 1,
                    client_nit: "1234567-8",
                    client_name: "Pedro López",
                    date_issued: "2024-02-01 14:00:00",
                    subtotal: 1525.00,
                    total_taxes: 122.00,
                    total: 1647.00,
                    currency: "USD",
                    state: "EMITTED",
                    dte_json: null,
                    dte_signature: null,
                    dte_traceability_code: null,
                    created_by: 3,
                    created_at: "2024-02-01 14:00:00"
                },
                {
                    id: 2,
                    invoice_type: "TICKET",
                    series: "T",
                    number: 1,
                    full_number: "T-001",
                    emitter_nit: "12345678-9",
                    emitter_name: "Mi Empresa POS",
                    client_id: 2,
                    client_nit: "9012345-6",
                    client_name: "Empresa ABC S.A.",
                    date_issued: "2024-02-02 15:00:00",
                    subtotal: 300.00,
                    total_taxes: 0.00,
                    total: 300.00,
                    currency: "USD",
                    state: "PAID",
                    dte_json: null,
                    dte_signature: null,
                    dte_traceability_code: null,
                    created_by: 3,
                    created_at: "2024-02-02 15:00:00"
                }
            ],
            // Líneas simuladas (separadas para combinar)
            lines: [
                {
                    id: 1,
                    invoice_id: 1,
                    product_id: 1,
                    description: "Laptop Dell",
                    quantity: 1.0000,
                    unit_price: 1500.0000,
                    discount: 0.0000,
                    tax_rate: 12.00,
                    line_total: 1500.0000,
                    warehouse_id: 1,
                    allocated: true
                },
                {
                    id: 2,
                    invoice_id: 1,
                    product_id: 2,
                    description: "Mouse Logitech",
                    quantity: 1.0000,
                    unit_price: 25.0000,
                    discount: 0.0000,
                    tax_rate: 12.00,
                    line_total: 25.0000,
                    warehouse_id: 1,
                    allocated: true
                },
                {
                    id: 3,
                    invoice_id: 2,
                    product_id: 3,
                    description: "Impresora HP",
                    quantity: 1.0000,
                    unit_price: 300.0000,
                    discount: 0.0000,
                    tax_rate: 0.00,
                    line_total: 300.0000,
                    warehouse_id: 1,
                    allocated: true
                }
            ]
        };
    },
    mounted() {
        this.loadFactura();
    },
    methods: {
        loadFactura() {
            const id = parseInt(this.$route.params.id);
            if (!id || id < 1 || id > this.invoices.length) {
                this.factura = null;
                return;
            }

            // Buscar la factura por ID
            const header = this.invoices.find(inv => inv.id === id);
            if (!header) {
                this.factura = null;
                return;
            }

            // Enriquecer con client_type del array clients
            const client = this.clients.find(c => c.id === header.client_id);
            header.client_type = client ? client.type : 'DESCONOCIDO';

            // Agregar líneas correspondientes del array lines
            const facturaLines = this.lines.filter(line => line.invoice_id === id);

            // Estructura final: { header, lines }
            this.factura = {
                header: header,
                lines: facturaLines
            };
        },
        formatDate(dateString) {
            const date = new Date(dateString);
            return date.toLocaleString('es-ES', { 
                day: '2-digit', 
                month: '2-digit', 
                year: 'numeric', 
                hour: '2-digit', 
                minute: '2-digit' 
            });
        },
        getStateClass(state) {
            return state === 'EMITTED' ? 'text-warning' : state === 'PAID' ? 'text-success' : 'text-danger';
        },
        getStateText(state) {
            return state === 'EMITTED' ? 'Emitida' : state === 'PAID' ? 'Pagada' : 'Cancelada';
        },
        goBack() {
            this.$router.push('/invoices');  // Asumiendo que la lista está en /facturacion
        },
        printPDF() {
            if (!this.factura) {
                alert('No hay datos de factura para imprimir.');
                return;
            }

            // Crear nuevo documento jsPDF (A4 portrait por default, optimizado para POS térmico)
            const doc = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: [80,200]
            });

            // Márgenes y fuente pequeña para POS (simula 80mm de ancho efectivo)
            const marginLeft = 1;
            const marginTop = 5;
            const pageWidth = doc.internal.pageSize.getWidth();
            //const availableWidth = pageWidth - (marginLeft * 2);

            // Fuente pequeña global
            doc.setFontSize(8);

            // 1. Header de Empresa (centrado)
            doc.setFontSize(12);
            doc.setFont(undefined, 'bold');
            doc.text(this.factura.header.emitter_name, pageWidth / 2, marginTop, { align: 'center' });
            doc.setFontSize(7);
            doc.setFont(undefined, 'normal');
            doc.text(`NIT: ${this.factura.header.emitter_nit}`, pageWidth / 2, marginTop + 5, { align: 'center' });

            // Línea separadora
            doc.setLineWidth(0.5);
            doc.line(marginLeft, marginTop + 10, pageWidth - marginLeft, marginTop + 10);

            let yPosition = marginTop + 15;

            // 2. Info de Factura
            doc.setFontSize(9);
            doc.setFont(undefined, 'bold');
            doc.text(`Factura: ${this.factura.header.full_number}`, marginLeft, yPosition);
            yPosition += 8;

            doc.setFontSize(7);
            doc.setFont(undefined, 'normal');
            doc.text(`Cliente: ${this.factura.header.client_name}`, marginLeft, yPosition);
            doc.text(`NIT: ${this.factura.header.client_nit}`, marginLeft, yPosition + 4);
            doc.text(`Fecha: ${this.formatDate(this.factura.header.date_issued)}`, marginLeft, yPosition + 8);
            doc.text(`Tipo: ${this.factura.header.invoice_type === 'FACTURA' ? 'Factura' : 'Ticket'}`, pageWidth - marginLeft, yPosition, { align: 'right' });
            doc.text(`Estado: ${this.getStateText(this.factura.header.state)}`, pageWidth - marginLeft, yPosition + 4, { align: 'right' });
            doc.text(`Moneda: ${this.factura.header.currency}`, pageWidth - marginLeft, yPosition + 8, { align: 'right' });
            yPosition += 15;

            // 3. Tabla de Detalles (usando autoTable para simplicidad y compactez)
            const tableData = this.factura.lines.map(line => [
                line.description,  // Descripción (ocupa más espacio)
                line.quantity.toFixed(2),  // Cantidad
                `$${line.unit_price.toFixed(2)}`,  // Precio Unitario
                `${line.tax_rate}%`,  // Impuesto
                `$${line.line_total.toFixed(2)}`  // Total Línea
            ]);

            doc.autoTable({
                head: [['Descripción', 'Cant.', 'P/U', 'Imp.', 'Total']],
                body: tableData,
                startY: yPosition,
                theme: 'grid',  // Bordes simples para POS
                styles: {
                    fontSize: 7,
                    cellPadding: 1,
                    overflow: 'linebreak',  // Para descripciones largas
                    halign: 'left',
                    valign: 'middle'
                },
                headStyles: {
                    fillColor: [255, 255, 255],  // Gris claro
                    textColor: [0, 0, 0],
                    fontStyle: 'bold',
                    fontSize: 7
                },
                columnStyles: {
                    0: { cellWidth: 35 },  // Descripción: 50% ancho
                    1: { cellWidth: 10, halign: 'center' },  // Cantidad: estrecha, centrada
                    2: { cellWidth: 12, halign: 'right' },  // Precio Unitario
                    3: { cellWidth: 8, halign: 'center' }, // Impuesto
                    4: { cellWidth: 15, halign: 'right' }   // Total Línea
                },
                margin: { left: marginLeft, right: marginLeft },
            });

            // 4. Totales
            let finalY = doc.lastAutoTable.finalY + 8;
            doc.setFontSize(9);
            doc.setFont(undefined, 'bold');
            doc.text('Totales', marginLeft, finalY);
            doc.setFontSize(8);
            doc.setFont(undefined, 'normal');

            finalY += 5;
            doc.text(`Subtotal: $${this.factura.header.subtotal.toFixed(2)}`, pageWidth - marginLeft, finalY, { align: 'right' });
            finalY += 5;
            doc.text(`Impuestos: $${this.factura.header.total_taxes.toFixed(2)}`, pageWidth - marginLeft, finalY, { align: 'right' });
            finalY += 5;
            doc.setFont(undefined, 'bold');
            doc.text(`Total: $${this.factura.header.total.toFixed(2)}`, pageWidth - marginLeft, finalY, { align: 'right' });

            // Línea separadora
            finalY += 8;
            doc.setLineWidth(0.3);
            doc.line(marginLeft, finalY, pageWidth - marginLeft, finalY);

            // 5. Firma / Pie
            finalY += 10;
            doc.setFontSize(6);
            doc.setFont(undefined, 'normal');
            doc.text(
                'Gracias por su compra.',
                pageWidth / 2,
                finalY,
                { align: 'center' }
            );
            finalY += 5;
            doc.text(
                'Esta factura cumple con los requisitos del Ministerio de Hacienda de El Salvador.',
                pageWidth / 2,
                finalY,
                { align: 'center' }
            );
            finalY += 5;
            doc.text(
                'Documento generado electrónicamente. No requiere firma autógrafa.',
                pageWidth / 2,
                finalY,
                { align: 'center' }
            );

            // 6. Abrir PDF en nueva pestaña
            window.open(doc.output('bloburl'), '_blank');
        }
    }
};
</script>